import { useMsal } from '@azure/msal-react';
import type { PortalRole } from '@denise-augusta/domain';

/**
 * Portal permissions.
 *
 * Roles come from Entra ID app role claims on the signed-in account:
 *   Portal.Admin  → 'admin'  — every portal feature
 *   Portal.Client → 'client' — only features released to clients
 *   no role       → null     — NO portal access
 *
 * Assign roles with infrastructure/scripts/grant-portal-admin.sh (which
 * also grants the storage data role admins need for blob writes). Users
 * must sign out and back in after a role is assigned — the roles claim is
 * baked into the ID token.
 */
export function resolvePortalRole(claims: unknown): PortalRole | null {
  const roles = (claims as { roles?: string[] } | undefined)?.roles ?? [];
  if (roles.includes('Portal.Admin')) return 'admin';
  if (roles.includes('Portal.Client')) return 'client';
  return null;
}

export function usePortalRole(): PortalRole | null {
  const { accounts } = useMsal();
  return resolvePortalRole(accounts[0]?.idTokenClaims);
}

/** admin sees everything; client sees only client features; null sees nothing. */
export function canAccess(userRole: PortalRole | null, required: PortalRole): boolean {
  if (userRole === null) return false;
  return userRole === 'admin' || required === 'client';
}

/** Map a portal role onto the lattice scope hierarchy (root > admin > user). */
export function roleToScope(role: PortalRole): string {
  return role === 'admin' ? 'admin' : 'user';
}

/** The lattice user context for the signed-in portal user. */
export function usePortalUser(): { id: string; scope: string; attributes: Record<string, unknown> } {
  const { accounts } = useMsal();
  const role = resolvePortalRole(accounts[0]?.idTokenClaims);
  return {
    id: accounts[0]?.username ?? 'portal-user',
    scope: role ? roleToScope(role) : 'user',
    attributes: { role },
  };
}
